/**
 * AnchoredPopoverComponent
 *
 * A self-contained custom element that manages a popover + trigger pair.
 * Adapted from the Shopify Horizon theme for use in Megastore (vanilla JS,
 * no external Component framework dependency).
 *
 * Features:
 *  - Hover-triggered open/close with a short delay to prevent flicker.
 *  - Uses the native HTML popover API (popover="auto").
 *  - Supports CSS Anchor Positioning (Chrome 125+) natively via the
 *    anchor-name / position-anchor inline styles set in Liquid.
 *  - For browsers without Anchor Positioning support, falls back to fixed
 *    positioning driven by CSS custom properties updated by JS.
 *  - Optionally closes the popover on window resize (data-close-on-resize).
 *
 * HTML structure expected:
 *
 *   <anchored-popover-component data-hover-triggered data-close-on-resize>
 *     <button data-ref="trigger" popovertarget="my-popover"
 *             style="anchor-name: --my-anchor;">…</button>
 *     <div    data-ref="popover"  id="my-popover" popover="auto"
 *             style="position-anchor: --my-anchor;">…</div>
 *   </anchored-popover-component>
 */
class AnchoredPopoverComponent extends HTMLElement {
  static INTERACTION_DELAY = 200;

  connectedCallback() {
    this._trigger = this.querySelector('[data-ref="trigger"]');
    this._popover = this.querySelector('[data-ref="popover"]');
    this._timeout = null;

    if (!this._trigger || !this._popover) return;

    if (this.hasAttribute("data-hover-triggered")) {
      this._trigger.addEventListener(
        "pointerenter",
        this._onTriggerEnter.bind(this)
      );
      this._trigger.addEventListener(
        "pointerleave",
        this._onTriggerLeave.bind(this)
      );
      this._popover.addEventListener(
        "pointerenter",
        this._onPopoverEnter.bind(this)
      );
      this._popover.addEventListener(
        "pointerleave",
        this._onPopoverLeave.bind(this)
      );
    }

    if (this.hasAttribute("data-close-on-resize")) {
      this._resizeHandler = this._debounce(() => {
        if (this._popover && this._popover.matches(":popover-open")) {
          this._popover.hidePopover();
        }
      }, 100);
      this._popover.addEventListener("beforetoggle", (e) => {
        if (e.newState === "open") {
          window.addEventListener("resize", this._resizeHandler);
        } else {
          window.removeEventListener("resize", this._resizeHandler);
        }
      });
    }

    // Fallback: update CSS custom properties for browsers without CSS Anchor
    // Positioning (Firefox, older Safari).
    if (!CSS.supports("position-anchor: --test")) {
      this._popover.addEventListener("beforetoggle", () =>
        this._updatePosition()
      );
      // Also calculate on idle so the panel is pre-positioned.
      const schedule =
        typeof requestIdleCallback === "function"
          ? requestIdleCallback
          : (fn) => setTimeout(fn, 0);
      schedule(() => this._updatePosition());
    }
  }

  disconnectedCallback() {
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler);
    }
    if (this._timeout) clearTimeout(this._timeout);
  }

  // ─── Hover handlers ──────────────────────────────────────────────────────

  _onTriggerEnter() {
    this._trigger.dataset.hoverActive = "true";
    if (!this._popover.matches(":popover-open")) {
      this._timeout = setTimeout(() => {
        if (this._trigger.dataset.hoverActive) this._popover.showPopover();
      }, AnchoredPopoverComponent.INTERACTION_DELAY);
    }
  }

  _onTriggerLeave() {
    delete this._trigger.dataset.hoverActive;
    if (this._timeout) clearTimeout(this._timeout);
    if (this._popover.matches(":popover-open")) {
      this._timeout = setTimeout(
        () => this._popover.hidePopover(),
        AnchoredPopoverComponent.INTERACTION_DELAY
      );
    }
  }

  _onPopoverEnter() {
    if (this._timeout) clearTimeout(this._timeout);
  }

  _onPopoverLeave() {
    this._timeout = setTimeout(
      () => this._popover.hidePopover(),
      AnchoredPopoverComponent.INTERACTION_DELAY
    );
  }

  // ─── Fallback positioning ─────────────────────────────────────────────────

  /**
   * Reads the trigger's bounding rect and writes CSS custom properties onto
   * the popover element so that the fallback CSS rules can position it.
   */
  _updatePosition() {
    const { _trigger: trigger, _popover: popover } = this;
    if (!trigger || !popover) return;

    const rect = trigger.getBoundingClientRect();
    popover.style.setProperty("--anchor-top", String(rect.top));
    popover.style.setProperty(
      "--anchor-right",
      String(window.innerWidth - rect.right)
    );
    popover.style.setProperty(
      "--anchor-bottom",
      String(window.innerHeight - rect.bottom)
    );
    popover.style.setProperty("--anchor-left", String(rect.left));
    popover.style.setProperty("--anchor-height", String(rect.height));
    popover.style.setProperty("--anchor-width", String(rect.width));
  }

  // ─── Utilities ────────────────────────────────────────────────────────────

  _debounce(fn, delay) {
    let timer = null;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }
}

if (!customElements.get("anchored-popover-component")) {
  customElements.define("anchored-popover-component", AnchoredPopoverComponent);
}
